// https://docs.getstrada.com/sdks/web/api-reference

import { useLazyQuery } from '@apollo/react-hooks';
import { LicenseStatusType } from 'Admin/components';
import { useAuth0 } from 'Auth';
import { loader } from 'graphql.macro';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { VALID_US_STATES_TYPE } from 'Shared/constants/states';
import { env } from 'Shared/constants/env';
import { getPageFromLocation } from 'Shared/utils';

type CurrentUserLicenses = {
  currentUser: {
    id: string;
    licenses: Array<{
      state: VALID_US_STATES_TYPE;
      status: LicenseStatusType;
    }>;
  };
};

export const GET_CURRENT_USER_LICENSES_QUERY = loader(
  './getCurrentUserLicensesQuery.graphql'
);

const useStrada = () => {
  // Track chat open state across component re-renders
  const [isOpen, changeIsOpen] = useState<boolean>(false);

  useEffect(() => {
    // Set up a custom event listener to track Strada chat state
    const handleStradaToggle = (event: CustomEvent<boolean>) => {
      changeIsOpen(event.detail);
    };

    window.addEventListener(
      'strada:toggle' as any,
      handleStradaToggle as EventListener
    );

    return () => {
      window.removeEventListener(
        'strada:toggle' as any,
        handleStradaToggle as EventListener
      );
    };
  }, []);

  return isOpen;
};

export const useStradaControls = () => {
  const isOpen = useStrada();
  const location = useLocation();
  const { user, getToken } = useAuth0();

  const [
    fetchLicenses,
    { data: userData, loading: userDataLoading, called: userDataCalled },
  ] = useLazyQuery<CurrentUserLicenses>(GET_CURRENT_USER_LICENSES_QUERY, {});

  const [openingChat, changeOpeningChat] = useState<boolean>(false);

  const stradaChat = window.stradaChat;
  const stradaLoaded = !!stradaChat;

  const isProduction = env.survicateEnvironment === 'production';

  const isMountedRef = useRef<boolean>(true);
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!userDataCalled) {
      return;
    }

    if (!userDataLoading && openingChat && stradaLoaded) {
      const page = getPageFromLocation(location);
      const licenses = (userData?.currentUser.licenses ?? [])
        .filter(license => license.status === 'valid_license')
        .map(license => license.state)
        .join(', ');

      getToken()
        .catch(() => null)
        .then(token => {
          if (!isMountedRef.current) {
            return;
          }

          changeOpeningChat(false);

          if (token) {
            try {
              stradaChat.setSecureMetadata({
                authorization: token,
              });
            } catch {
              // If setting secure metadata fails, continue to open chat
            }
          }

          try {
            stradaChat.setAgentVariables({
              first_name: user?.firstName,
              last_name: user?.lastName,
              email: user?.email,
              agency: user?.partner?.name,
              chatter_type: 'agent',
              page_when_chat_opened: page,
              licenses,
              ...(!isProduction && { debug: true }),
            });
          } catch {
            // If setting agent variables fails, continue to open chat
          }

          stradaChat.open();

          window.dispatchEvent(
            new CustomEvent('strada:toggle', { detail: true })
          );
        });
    }
  }, [
    openingChat,
    userDataCalled,
    userDataLoading,
    userData,
    user,
    location,
    stradaChat,
    stradaLoaded,
    changeOpeningChat,
    getToken,
    isProduction,
  ]);

  const toggleOpen = () => {
    if (stradaLoaded && !userDataLoading && !openingChat) {
      if (!isOpen) {
        // This will trigger the useEffect above to set metadata and open chat
        changeOpeningChat(true);
        fetchLicenses();
      } else {
        stradaChat.close();
        window.dispatchEvent(
          new CustomEvent('strada:toggle', { detail: false })
        );
      }
    }
  };

  return { loaded: stradaLoaded, toggleOpen, isOpen };
};
